import React, { Suspense, lazy } from 'react';
import { Route, Navigate, Routes } from 'react-router-dom';
import { withRouter } from '../util/withRouter';
import { connect } from 'react-redux';
import CircularProgress from '@mui/material/CircularProgress';
import Header from '../components/Header/index';
import Sidebar from '../containers/SideNav/index';
import Footer from '../components/Footer';
import TopNav from '../components/TopNav';
import { isIOS, isMobile } from 'react-device-detect';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PushNotificationManager from '../components/Notifications/PushNotificationManager';
import { Axios, ApiUrl } from '../components/';
import {
  ABOVE_THE_HEADER,
  BELOW_THE_HEADER,
  COLLAPSED_DRAWER,
  FIXED_DRAWER,
  HORIZONTAL_NAVIGATION,
} from '../constants/ActionTypes';

// ─── Lazy-loaded Route Modules ────────────────────────────────────────────────
// Each route chunk is code-split for faster initial page load.
// The Suspense fallback shows a spinner while the chunk loads.
const Dashboard = lazy(() => import('./routes/dashboard'));
const Customers = lazy(() => import('./routes/customers'));
const Drivers = lazy(() => import('./routes/drivers'));
const CarTypes = lazy(() => import('./routes/car-types'));
const Trips = lazy(() => import('./routes/trips'));
const Reviews = lazy(() => import('./routes/reviews'));
const PromoCodes = lazy(() => import('./routes/promo-codes'));
const AdminUser = lazy(() => import('./routes/users'));
const PartnersUser = lazy(() => import('./routes/partners'));
const SocketUser = lazy(() => import('./routes/socket'));
const PushNotification = lazy(() => import('./routes/notification'));
const Faq = lazy(() => import('./routes/faq'));
const ContentPage = lazy(() => import('./routes/contentPages'));
const Settings = lazy(() => import('./routes/settings'));
const Earnings = lazy(() => import('./routes/earnings'));
const Advertisement = lazy(() => import('./routes/advertisement'));
const Banner = lazy(() => import('./routes/banner'));
const Invoices = lazy(() => import('./routes/invoice'));
const GeoFencing = lazy(() => import('./routes/geofencing'));
const ManageCar = lazy(() => import('./routes/drivers/routes/cars/index'));
const AddCar = lazy(() => import('./routes/drivers/routes/cars/addCar.js'));
const AllVehicles = lazy(() => import('./routes/drivers/routes/cars/allCars.js'));
const Notifications = lazy(() => import('./routes/notifications/MessagesPage.js'));
const SupportChat = lazy(() => import('./routes/supportChat'));
const NotFoundPage = lazy(() => import('./routes/extraPages/routes/404'));

const { API_GET_NOTIFICATION_COUNT, API_GET_ADMIN_PROFILE } = ApiUrl;

/** Guard to prevent admins without Support Chat access from viewing the route */
const ProtectedSupportChatRoute = () => {
  const isAllowed = localStorage.getItem('isSupportChat') !== 'false';
  if (!isAllowed) {
    return <Navigate to="/app/dashboard/index" replace />;
  }
  return <SupportChat />;
};

/** Full-screen loading fallback shown while a lazy route chunk is fetching */
const RouteFallback = () => (
  <div
    style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}
  >
    <CircularProgress />
  </div>
);

class App extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      unreadCount: 0,
      isSupportChat: localStorage.getItem('isSupportChat') !== 'false',
    };
    this.fetchUnreadCount = this.fetchUnreadCount.bind(this);
    this.fetchAdminProfile = this.fetchAdminProfile.bind(this);
  }

  componentDidMount() {
    this.fetchUnreadCount();
    this.fetchAdminProfile();
  }

  async fetchAdminProfile() {
    try {
      const res = await Axios.get(API_GET_ADMIN_PROFILE);
      if (res.data && res.data.status === 1 && res.data.data) {
        const { isSuperAdmin, isSupportChat, name, role } = res.data.data;
        const currentSupportChat = isSupportChat !== false;
        localStorage.setItem('isSuperAdmin', isSuperAdmin ? 'true' : 'false');
        localStorage.setItem('isSupportChat', currentSupportChat ? 'true' : 'false');
        if (name) localStorage.setItem('name', name);
        if (role) localStorage.setItem('role', role);
        this.setState({ isSupportChat: currentSupportChat });
        window.dispatchEvent(
          new CustomEvent('admin_profile_updated', {
            detail: { isSupportChat: currentSupportChat, isSuperAdmin: Boolean(isSuperAdmin) },
          })
        );
      }
    } catch (err) {
      // Ignored if offline or unauthorized
    }
  }

  async fetchUnreadCount() {
    try {
      const res = await Axios.get(API_GET_NOTIFICATION_COUNT);
      if (res.status === 200) {
        this.setState({ unreadCount: res.data.unreadCount > 9 ? '9+' : res.data.unreadCount });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch unread count', err);
    }
  }

  render() {
    const { drawerType, navigationStyle, horizontalNavPosition } = this.props;
    const drawerStyle = drawerType.includes(FIXED_DRAWER)
      ? 'fixed-drawer'
      : drawerType.includes(COLLAPSED_DRAWER)
        ? 'collapsible-drawer'
        : 'mini-drawer';

    if (isIOS && isMobile) {
      document.body.classList.add('ios-mobile-view-height');
    } else if (document.body.classList.contains('ios-mobile-view-height')) {
      document.body.classList.remove('ios-mobile-view-height');
    }

    return (
      <div className={`app-container ${drawerStyle}`}>
        <Sidebar />
        <div className="app-main-container">
          <div
            className={`app-header ${
              navigationStyle === HORIZONTAL_NAVIGATION ? 'app-header-horizontal' : ''
            }`}
          >
            {navigationStyle === HORIZONTAL_NAVIGATION &&
              horizontalNavPosition === ABOVE_THE_HEADER && <TopNav styleName="app-top-header" />}
            <Header unreadCount={this.state.unreadCount} />
            {navigationStyle === HORIZONTAL_NAVIGATION &&
              horizontalNavPosition === BELOW_THE_HEADER && <TopNav />}
          </div>

          <main className="app-main-content-wrapper">
            <div className="app-main-content">
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route path="/" element={<Navigate to="dashboard/index" replace />} />

                  <Route path="dashboard/*" element={<Dashboard />} />
                  <Route path="support-chat/*" element={<ProtectedSupportChatRoute />} />
                  <Route path="drivers/*" element={<Drivers />} />
                  <Route path="customers/*" element={<Customers />} />
                  <Route path="trips/*" element={<Trips />} />
                  <Route path="reviews/*" element={<Reviews />} />
                  <Route path="car-types/*" element={<CarTypes />} />
                  <Route path="invoice/*" element={<Invoices />} />

                  <Route
                    path="all-notifications"
                    element={<Notifications onReadCountChanged={() => this.fetchUnreadCount()} />}
                  />

                  <Route path="geofencing/*" element={<GeoFencing />} />
                  <Route path="advertisement/*" element={<Advertisement />} />
                  <Route path="banner/*" element={<Banner />} />
                  <Route path="promo-codes/*" element={<PromoCodes />} />
                  <Route path="notification/*" element={<PushNotification />} />
                  <Route path="contentPages/*" element={<ContentPage />} />
                  <Route path="faq/*" element={<Faq />} />
                  <Route path="earnings/*" element={<Earnings />} />
                  <Route path="manageCars/cars/:editId" element={<ManageCar />} />
                  <Route path="manageCars/car/add/:driverId/:carId?" element={<AddCar />} />
                  <Route path="all/vehicles" element={<AllVehicles />} />
                  <Route path="users/*" element={<AdminUser />} />
                  <Route path="partners/*" element={<PartnersUser />} />
                  <Route path="socket/*" element={<SocketUser />} />
                  <Route path="settings/*" element={<Settings />} />

                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </div>
            <Footer />
            <ToastContainer autoClose={2000} />
            <PushNotificationManager />
          </main>
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({ settings }) => {
  const { drawerType, navigationStyle, horizontalNavPosition } = settings;
  return { drawerType, navigationStyle, horizontalNavPosition };
};

export default withRouter(connect(mapStateToProps)(App));
